import { DIALOG_DATA } from '@angular/cdk/dialog'

import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnDestroy, OnInit, signal } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import {
  PalantirButtonComponent,
  PalantirDialogComponent,
  PalantirDialogRef,
  PalantirIconComponent,
  PalantirInputComponent,
  PalantirListItemComponent,
  PalantirSkeletonComponent,
  PalantirTextComponent,
} from '@fieldcontrol/palantir'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs'
import { MaintenanceProductItem } from '../../maintenance-products-tab.type'
import { MaintenanceProductsTabService } from '../../services/maintenance-products-tab.service'

@Component({
  selector: 'app-products-search-dialog',
  templateUrl: './products-search-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    PalantirDialogComponent,
    PalantirButtonComponent,
    PalantirTextComponent,
    PalantirListItemComponent,
    PalantirIconComponent,
    PalantirInputComponent,
    PalantirSkeletonComponent,
    NgScrollbarModule,
    TranslateModule
],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductsSearchDialogComponent implements OnInit, OnDestroy {
  private readonly data = inject(DIALOG_DATA)
  private readonly palantirDialogRef = inject(PalantirDialogRef)
  public readonly productsTabService = inject(MaintenanceProductsTabService)

  private readonly destroy$ = new Subject<void>()

  public products = signal<MaintenanceProductItem[]>([])
  public selectedItems = signal<MaintenanceProductItem[]>([])
  public searchControl = new FormControl('')

  public ngOnInit(): void {
    const preSelected: MaintenanceProductItem[] = this.data?.selectedItems ?? []
    this.selectedItems.set([...preSelected])

    this.searchControl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(async () => {
      await this.search()
    })

    void this.search()
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public isSelected(id: string): boolean {
    return this.selectedItems().some((item) => item.id === id)
  }

  public toggleItem(item: MaintenanceProductItem): void {
    const current = this.selectedItems()
    if (this.isSelected(item.id)) {
      this.selectedItems.set(current.filter((existing) => existing.id !== item.id))
    } else {
      this.selectedItems.set([...current, item])
    }
  }

  public async close(): Promise<void> {
    await this.palantirDialogRef.close()
  }

  public async save(): Promise<void> {
    await this.palantirDialogRef.close({ selectedItems: this.selectedItems() })
  }

  private async search(): Promise<void> {
    this.productsTabService.filterForm.patchValue({ name_contains: this.searchControl.value })
    this.productsTabService.offset.set(0)
    this.products.set(await this.productsTabService.searchPage())
  }
}
